/**
 * Ce qui survit à un rechargement de page — et rien d'autre.
 *
 * Le magasin de session vit dans le tas JavaScript. Un `F5` détruit ce tas :
 * sans ce module, l'opérateur repassait par le mot de passe **et** le code
 * reçu par SMS à chaque rafraîchissement. C'est le défaut signalé par le
 * porteur, et il coûte un SMS facturé par accident de souris.
 *
 * ## Pourquoi `sessionStorage`, et pas autre chose
 *
 * `sessionStorage` est **exactement** de la portée qu'on veut : il survit à un
 * rechargement et meurt avec l'onglet. `localStorage`, lui, survivrait à la
 * fermeture du navigateur et donnerait la vacation d'un agent à qui rouvre le
 * poste le lendemain — c'est pourquoi ce module ne l'écrit jamais, et pourquoi
 * une suite le vérifie sur le stockage réel.
 *
 * ## Ce qu'on perd, dit honnêtement
 *
 * `sessionStorage` est **plus faible que la mémoire** face à une injection de
 * script. Plus faible, mais de peu : du script injecté dans la page lit un
 * magasin Zustand aussi bien qu'il lit `sessionStorage` — dans les deux cas il
 * lui suffit d'attendre que l'agent se connecte. L'écart réel entre les deux
 * est la **fenêtre** — le stockage rend le jeton lisible avant même que l'agent
 * ne touche l'écran —, et le prix de l'ancienne règle était une reconnexion
 * complète, second facteur compris, à chaque `F5`.
 *
 * **La vraie protection est ailleurs, et ce fichier ne la met pas en œuvre :**
 * un cookie `httpOnly; Secure; SameSite=Strict`, qu'aucun script ne peut lire,
 * ni celui de l'application ni celui d'un attaquant. Elle demande une anti-CSRF
 * (le navigateur joindrait le cookie à toute requête, y compris celle d'un
 * autre site) et une modification du serveur, qui rend aujourd'hui le jeton
 * dans le corps de la réponse. C'est la suite à écrire, pas une idée écartée :
 * tant qu'elle n'est pas là, la surface de risque est celle décrite ci-dessus.
 *
 * ## Restaurer, c'est vérifier — jamais croire
 *
 * Ce qui sort du stockage est une **prétention**, pas un fait : périmé,
 * révoqué, ou réécrit à la main par qui a la console ouverte. D'où trois
 * refus, tous éprouvés :
 *
 *  1. une charge illisible, tronquée ou d'une autre forme n'est pas restaurée ;
 *  2. une échéance d'échange **passée ou illisible** n'est pas restaurée : plus
 *     rien ne peut la sauver, et la restaurer donnerait une coquille qui se
 *     remplit de `401` avant de retomber sur l'écran de connexion ;
 *  3. tout refus va de pair avec un **effacement** : un jeton mort qui reste
 *     écrit se relit à chaque rechargement.
 *
 * **Le jeton d'accès périmé, lui, n'est pas un motif de refus.** C'est le
 * travail de `session-renewal.ts`, livré avant ce module : il échange le couple
 * *avant* l'appel dès que l'échéance approche. Refuser ici une session dont
 * seul l'accès est périmé rejouerait cette décision à un autre endroit — deux
 * règles pour un même fait, qui divergeront. La restauration ne regarde donc
 * que l'échéance d'**échange**, celle au-delà de laquelle le renouvellement
 * lui-même est impossible.
 */

import type { OperatorSession } from './session-store.js';

/**
 * La clé sous laquelle la session est écrite.
 *
 * Préfixée par l'application : un back-office et un autre outil servis depuis
 * la même origine partageraient le stockage, et une clé nue comme `session` se
 * ferait écraser sans que rien ne le dise.
 */
export const sessionStorageKey = 'jeliko.backoffice.session';

/**
 * Les champs écrits, et les seuls.
 *
 * Cette table est **éprouvée sur son contenu**, entrée par entrée : une charge
 * privée de l'un quelconque d'entre eux doit être refusée. Sans cela, une
 * charge à moitié écrite — un onglet fermé au milieu d'un `setItem`, une
 * édition à la main — restaurerait une session dont un jeton vaut `undefined`,
 * et l'écran partirait appeler le serveur avec.
 *
 * Elle sert aussi à l'écriture : on **recopie champ par champ** au lieu de
 * sérialiser l'objet reçu. Le jour où la réponse du serveur portera un champ de
 * plus, il ne partira pas dans le stockage par le seul fait d'exister.
 */
export const persistedSessionFields = [
  'token',
  'refreshToken',
  'expiresAt',
  'refreshExpiresAt',
  'role',
  'displayName',
] as const satisfies readonly (keyof OperatorSession)[];

/**
 * Le strict nécessaire d'un stockage de navigateur.
 *
 * Une interface structurelle, et non le type `Storage` : un test doit pouvoir
 * en fournir un qui lève, ce que `Storage` ne permet pas d'écrire simplement.
 */
export interface SessionStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Le `sessionStorage` du navigateur, ou `null` s'il est hors d'atteinte.
 *
 * **L'accès lui-même peut lever** : navigation privée sur Safari, navigateur
 * réglé pour bloquer les données de site, page servie dans un cadre cloisonné.
 * Un `null` rendu ici fait retomber l'application sur son comportement d'avant
 * — la session vit en mémoire, un rechargement la perd — au lieu de faire un
 * écran blanc au démarrage sur un poste dont personne ne saura dire ce qu'il a
 * de particulier.
 */
export function browserSessionStorage(): SessionStorageLike | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    // Le seul fait de lire la propriété a levé : il n'y a pas de stockage ici.
    return null;
  }
}

/**
 * Ne garde que les champs du contrat, et refuse tout ce qui n'est pas une
 * chaîne pleine. Rend `null` dès qu'un seul manque.
 *
 * **La même fonction sert à lire et à écrire**, et c'est voulu : une charge
 * qu'on écrirait sans pouvoir la relire est un jeton laissé au repos sur un
 * poste partagé, en échange de rien.
 */
function champsRetenus(source: object): Record<string, string> | null {
  const champs = source as Record<string, unknown>;
  const retenu: Record<string, string> = {};
  for (const nom of persistedSessionFields) {
    const valeur = champs[nom];
    if (typeof valeur !== 'string' || valeur === '') return null;
    retenu[nom] = valeur;
  }
  return retenu;
}

function lireLaCharge(brut: string): OperatorSession | null {
  let charge: unknown;
  try {
    charge = JSON.parse(brut);
  } catch {
    // Du JSON corrompu n'est pas une session vide : c'est une charge à jeter.
    return null;
  }
  if (typeof charge !== 'object' || charge === null || Array.isArray(charge)) return null;
  return champsRetenus(charge) as unknown as OperatorSession | null;
}

/**
 * La session écrite dans [storage], si elle est encore défendable.
 *
 * Rend `null` dans tous les autres cas, et **efface** ce qu'elle refuse.
 */
export function readStoredSession(
  storage: SessionStorageLike | null,
  now: () => number = () => Date.now(),
): OperatorSession | null {
  if (storage === null) return null;

  let brut: string | null;
  try {
    brut = storage.getItem(sessionStorageKey);
  } catch {
    // Un stockage qui lève à la lecture n'a rien à restaurer, et rien à effacer.
    return null;
  }
  if (brut === null) return null;

  const candidate = lireLaCharge(brut);
  if (candidate === null) {
    clearStoredSession(storage);
    return null;
  }

  // L'échéance d'ÉCHANGE, pas celle de l'accès : voir l'en-tête. Une date
  // illisible est traitée comme passée — c'est une prétention invérifiable, et
  // la seule issue sûre d'une vérification impossible est le refus.
  const echeance = Date.parse(candidate.refreshExpiresAt);
  if (Number.isNaN(echeance) || echeance <= now()) {
    clearStoredSession(storage);
    return null;
  }

  return candidate;
}

/** Écrit [session] dans [storage]. Un stockage indisponible n'est pas une erreur. */
export function writeStoredSession(
  storage: SessionStorageLike | null,
  session: OperatorSession,
): void {
  if (storage === null) return;
  const charge = champsRetenus(session);
  // Une session incomplète — un serveur qui n'aurait pas rendu le couple entier —
  // n'est pas écrite : elle serait refusée à la relecture, et le jeton aurait
  // dormi sur le poste sans jamais servir.
  if (charge === null) return;
  try {
    storage.setItem(sessionStorageKey, JSON.stringify(charge));
  } catch {
    // Quota, navigation privée, données de site bloquées : la session reste en
    // mémoire et l'onglet fonctionne. Seul le rechargement redemandera tout.
  }
}

/**
 * Efface la session écrite.
 *
 * **Ce n'est pas une commodité.** Les agents du back-office se relaient sur les
 * mêmes postes : un jeton qui survivrait à une déconnexion serait un défaut
 * pire que celui que la persistance corrige.
 */
export function clearStoredSession(storage: SessionStorageLike | null): void {
  if (storage === null) return;
  try {
    storage.removeItem(sessionStorageKey);
  } catch {
    // Rien à faire de plus : il n'y avait pas de stockage utilisable.
  }
}
